// Calls report for company staff (Phase 8; any role may view): every live
// call the company made per day in a billing month, how many disclosed data
// and what was billed. Every call that disclosed nothing is one "not found"
// count: the reasons are never shown to a business (P2-1).
import { Group, Loader, NativeSelect, Paper, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError } from "../../shared/ui/Page";
import { billingMonths, monthRange } from "../../shared/ui/months";
import { companyKey } from "./common";

export function CallsTab({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const months = useMemo(() => billingMonths(), []);
  const [selected, setSelected] = useState(months[0]?.value ?? "");
  const month = months.find((m) => m.value === selected) ?? months[0];
  const range = month ? monthRange(month) : { from: "" };
  const report = useQuery({
    queryKey: companyKey(companyId, "calls", selected),
    queryFn: () =>
      unwrap(
        api.GET("/companies/{company_id}/billing/calls", {
          params: { path: { company_id: companyId }, query: range },
        }),
      ),
  });
  const n = (v: number) => v.toLocaleString(i18n.language);
  const monthLabel = (year: number, m: number) =>
    new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric", timeZone: "UTC" }).format(
      new Date(Date.UTC(year, m, 1)),
    );
  const r = report.data;
  const days = [...(r?.days ?? [])].reverse(); // newest first

  return (
    <Paper withBorder p="md" radius="md" data-testid="calls-report">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2} size="h4">
            {t("calls.title")}
          </Title>
          <Text size="sm" c="dimmed">
            {t("calls.intro")}
          </Text>
        </Stack>
        <Group>
          <NativeSelect
            label={t("billing.month")}
            value={selected}
            onChange={(e) => setSelected(e.currentTarget.value)}
            data={months.map((m) => ({ value: m.value, label: monthLabel(m.year, m.month) }))}
          />
        </Group>
        {report.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : !r ? (
          <FormError message={errorMessage(report.error, t("error.generic"))} />
        ) : (
          <>
            <SimpleGrid cols={{ base: 2, sm: 4 }} data-testid="calls-totals">
              <Stat label={t("calls.calls")} value={n(r.totals.calls)} />
              <Stat label={t("calls.disclosed")} value={n(r.totals.disclosed)} />
              <Stat label={t("calls.not_found")} value={n(r.totals.not_found)} />
              <Stat label={t("calls.billed")} value={f.money(r.totals.amount_minor, r.currency)} />
            </SimpleGrid>
            {days.length === 0 ? (
              <Text size="sm" c="dimmed" data-testid="calls-empty">
                {t("calls.empty")}
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={560}>
                <Table verticalSpacing="xs" aria-label={t("calls.title")}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("billing.day")}</Table.Th>
                      <Table.Th ta="right">{t("calls.calls")}</Table.Th>
                      <Table.Th ta="right">{t("calls.disclosed")}</Table.Th>
                      <Table.Th ta="right">{t("calls.not_found")}</Table.Th>
                      <Table.Th ta="right">{t("calls.billable")}</Table.Th>
                      <Table.Th ta="right">{t("billing.amount")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {days.map((d) => (
                      <Table.Tr key={d.day} data-testid="calls-row">
                        <Table.Td>{d.day ? f.day(d.day) : ""}</Table.Td>
                        <Table.Td ta="right">{n(d.calls)}</Table.Td>
                        <Table.Td ta="right">{n(d.disclosed)}</Table.Td>
                        <Table.Td ta="right">{n(d.not_found)}</Table.Td>
                        <Table.Td ta="right">{n(d.billable)}</Table.Td>
                        <Table.Td ta="right">{f.money(d.amount_minor, r.currency)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
            <Text size="xs" c="dimmed">
              {t("calls.privacy_note")}
            </Text>
          </>
        )}
      </Stack>
    </Paper>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Stack gap={0}>
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="xl" fw={700}>
        {value}
      </Text>
    </Stack>
  );
}
